import {EventConstants} from 'constants/EventConstants';
import {User} from 'constants/UserConstants';
import * as context from 'Context';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import {ServerEntryVersion, ToServerId} from 'notebook/ELNConstants';
import {addComputedTagValuesToNote} from 'notebook/integrations/registry/RegistrationActionUtil';
import VersionedItemSubstore from 'notebook/store/VersionedItemSubstore';
import {removeQueryResolutionsForTableFromSnapshotInNote} from 'notebook/tables/excess/Resolver';
import {EntryNote, NotePart} from 'notebook/util/NoteUtilConstants';
import BaseEntryStore from 'stores/BaseEntryStore';
import {ServerStageEntryDocument, StageEntryDocument} from 'workflows/WorkflowConstants';

import StageEntryUpdater from './StageEntryUpdater';
import * as StageEntryUtil from './StageEntryUtil';

export interface StageEntryStoreVersion {
  parentId: number | null;
  created_at: string;
  modified_at: string;
  creator: User;
  id: number;
}

/**
 * This store is used for storing StageEntries with the nested EntryVersion.
 */
// TODO(T10437): rename to WorkflowEntryStore
class StageEntryStore extends BaseEntryStore<
  StageEntryDocument,
  ServerStageEntryDocument,
  StageEntryStoreVersion,
  'stageEntryDocument',
  'stageEntryId'
> {
  postSanitizeItem(
    id: string,
    newEntry: StageEntryDocument,
    oldEntry: StageEntryDocument | null,
    {bumpVersion}: {bumpVersion: boolean}
  ): StageEntryDocument {
    const updater = new StageEntryUpdater(
      id,
      () => this.getEntry(id),
      (entryVersion) => {
        const StageEntryActions = require('./StageEntryActions');
        StageEntryActions.update(id, {$merge: {entryVersion}}, {bumpVersion});
      }
    );
    return updater.run(newEntry, oldEntry);
  }

  createItemSubstore(): VersionedItemSubstore<
    StageEntryDocument,
    NotePart,
    ServerStageEntryDocument,
    StageEntryStoreVersion,
    'stageEntryDocument',
    'stageEntryId'
  > {
    return new VersionedItemSubstore<
      StageEntryDocument,
      NotePart,
      ServerStageEntryDocument,
      StageEntryStoreVersion,
      'stageEntryDocument',
      'stageEntryId'
    >({
      options: {
        idKey: 'stageEntryId',
        itemKey: 'stageEntryDocument',
        getVersionCid(item: StageEntryDocument): string {
          return item.entryVersion.cid;
        },
        asyncItemLoad: true,
        sanitizeItem: StageEntryUtil.sanitizeStageEntry,
        postSanitizeItem: this.postSanitizeItem.bind(this),
        // TODO(T8957): Add support for postProcess and invertPostProcess for file attachemnts
        prepareForStore: StageEntryUtil.prepareForStore,
        pickRevertableFields: StageEntryUtil.pickRevertableFields,
        doSave(id: string): void {
          const StageEntryActions = require('./StageEntryActions');
          return StageEntryActions.save(id);
        },
      },
      events: {
        item: {
          load: {
            init: EventConstants.WORKFLOW_STAGE_ENTRY_DOCUMENT_FETCH_STARTED,
            completed: EventConstants.WORKFLOW_STAGE_ENTRY_DOCUMENT_FETCH_COMPLETED,
            failed: EventConstants.WORKFLOW_STAGE_ENTRY_DOCUMENT_FETCH_FAILED,
          },
          registerObjects: EventConstants.WORKFLOW_STAGE_ENTRY_REGISTER_OBJECTS,
          close: EventConstants.WORKFLOW_STAGE_ENTRY_CLOSE,
          dirty: EventConstants.WORKFLOW_STAGE_ENTRY_INVALIDATE,
          revert: EventConstants.WORKFLOW_STAGE_ENTRY_REVERT,
          update: EventConstants.WORKFLOW_STAGE_ENTRY_UPDATE,
          save: {
            init: EventConstants.WORKFLOW_STAGE_ENTRY_SAVE,
            completed: EventConstants.WORKFLOW_STAGE_ENTRY_SAVE_COMPLETED,
            failed: EventConstants.WORKFLOW_STAGE_ENTRY_SAVE_FAILED,
          },
        },
        version: {
          load: {
            init: EventConstants.WORKFLOW_STAGE_ENTRY_LOAD_VERSION,
            completed: EventConstants.WORKFLOW_STAGE_ENTRY_LOAD_VERSION_COMPLETED,
            failed: EventConstants.WORKFLOW_STAGE_ENTRY_LOAD_VERSION_FAILED,
          },
        },
        versions: {
          fetch: {
            init: EventConstants.WORKFLOW_STAGE_ENTRY_FETCH_VERSIONS,
            completed: EventConstants.WORKFLOW_STAGE_ENTRY_FETCH_VERSIONS_COMPLETED,
            failed: EventConstants.WORKFLOW_STAGE_ENTRY_FETCH_VERSIONS_FAILED,
          },
        },
      },
    });
  }

  getNote(id: string): EntryNote {
    return this.itemSubstore.getItemStateUnsafe(id).stageEntryDocument.entryVersion.note;
  }

  getEntry(id: string): StageEntryDocument | null {
    if (!this.inStore(id)) {
      return null;
    }
    return this.itemSubstore.getItemStateUnsafe(id).stageEntryDocument;
  }

  override payloadHandler(action: FluxAction): boolean {
    if (action.actionType === EventConstants.COMPUTED_TAGS_UPDATED_AFTER_REGISTRATION_ACTION) {
      const {entryId, fileSummariesById, provenanceUuid} = action;
      if (!this.inStore(entryId)) {
        return true;
      }

      const {stageEntryDocument} = this.itemSubstore.getItemStateUnsafe(entryId);
      const newStageEntryDocument: StageEntryDocument = {
        ...stageEntryDocument,
        entryVersion: {
          ...stageEntryDocument.entryVersion,
          note: addComputedTagValuesToNote(
            stageEntryDocument.entryVersion.note,
            provenanceUuid,
            fileSummariesById
          ),
        },
      };
      this.itemSubstore.updateItem(entryId, newStageEntryDocument, {bumpVersion: true});
      // Clear history to prevent undoing computed field updates
      this.itemSubstore._clearHistory(entryId);
      this.emitChange();
      return true;
    }

    if (action.actionType === EventConstants.EXCESS_DELETE_SNAPSHOTTED_RESOLUTIONS_FOR_TABLE) {
      const {itemId, provenanceUuid, lookupConfigIds} = action;
      if (!this.inStore(itemId)) {
        return true;
      }

      const {stageEntryDocument} = this.itemSubstore.getItemStateUnsafe(itemId);
      const newStageEntryDocument = {
        ...stageEntryDocument,
        entryVersion: {
          ...stageEntryDocument.entryVersion,
          note: removeQueryResolutionsForTableFromSnapshotInNote(
            stageEntryDocument.entryVersion.note,
            provenanceUuid,
            lookupConfigIds
          ),
        },
      };

      this.itemSubstore.updateItem(itemId, newStageEntryDocument, {bumpVersion: true});
      this.emitChange();
      return true;
    }

    if (action.actionType === EventConstants.EXCESS_FORCE_EVALUATION) {
      const {fileId} = action;
      // If the entry isn't in the store anymore we can skip evaluation.
      if (!this.inStore(fileId)) {
        return true;
      }

      const {stageEntryDocument} = this.itemSubstore.getItemStateUnsafe(fileId);
      const updater = new StageEntryUpdater(
        fileId,
        () => this.getEntry(fileId),
        (entryVersion) => {
          const StageEntryActions = require('./StageEntryActions');
          StageEntryActions.update(fileId, {$merge: {entryVersion}}, {bumpVersion: false});
        }
      );
      context.using({forceExcessRevaluate: true}, this, () =>
        updater.run(stageEntryDocument, stageEntryDocument)
      );

      this.emitChange();
      return true;
    }

    if (action.actionType === EventConstants.WORKFLOW_STAGE_ENTRY_SERVER_SIDE_UPDATE) {
      const {stageEntryId} = action;
      if (this.inStore(stageEntryId)) {
        this.emitChange();
      }

      return true;
    }

    // @ts-expect-error ts(2345)
    const shouldEmit = this.itemSubstore.handlePayload(action);
    if (shouldEmit) {
      this.emitChange();
    }
    return true;
  }

  prepareForSave(
    id: string,
    stageEntry: StageEntryDocument
  ): {name: string; entryVersion: ServerEntryVersion<ToServerId>} {
    const {cidIdMap} = this.itemSubstore.getItemStateUnsafe(id);
    return StageEntryUtil.prepareForSave(stageEntry, cidIdMap);
  }

  __serverBootstrapItem(object: {id: string; item: ServerStageEntryDocument}): void {
    this.itemSubstore.__serverBootstrapItem(object);
  }
}

const stageEntryStore = new StageEntryStore(AppDispatcher);

export default stageEntryStore;
